import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { Platform } from 'react-native';

export function isMetroHmrNotReadyError(error: unknown): boolean {
	return (
		error instanceof Error &&
		error.message.includes('HMRClient.setup')
	);
}

function waitForMetroReady(): Promise<void> {
	if (!__DEV__) {
		return Promise.resolve();
	}

	// Native dev loads lazy chunks before HMRClient.setup() finishes on cold start.
	const initialDelayMs = Platform.OS === 'web' ? 0 : 250;
	return new Promise((resolve) => setTimeout(resolve, initialDelayMs));
}

/**
 * Retries dynamic imports that fail in dev when Metro HMR is not ready yet.
 */
export async function importAfterMetroReady<T>(
	loader: () => Promise<T>,
	maxAttempts = 12,
	delayMs = 150
): Promise<T> {
	let lastError: unknown;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		if (attempt > 0) {
			await new Promise((resolve) =>
				setTimeout(resolve, delayMs * attempt)
			);
		} else {
			await waitForMetroReady();
		}

		try {
			return await loader();
		} catch (error) {
			lastError = error;
			if (
				!__DEV__ ||
				!isMetroHmrNotReadyError(error) ||
				attempt === maxAttempts - 1
			) {
				throw error;
			}
		}
	}

	throw lastError;
}

export function lazyAfterMetroReady<T extends ComponentType<any>>(
	factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
	return lazy(() => importAfterMetroReady(factory));
}
